import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { RequestStoreService } from '../services/request-store.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(private authService: AuthService, private requestStore: RequestStoreService,) { }

  ieFix = {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    Expires: 'Thu, 01 Jan 1970 00:00:00 GMT',
    'If-Modified-Since': '0'
  };

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (request.url.toLowerCase().indexOf('amazonaws.com') > -1) {
      request = request.clone({
        setHeaders: this.ieFix
      });
      return next.handle(request);
    }
    // add authorization header with basic auth credentials if available
    const currentUser = this.authService.currentUser;
    if (currentUser) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${this.authService.access_token}`,
          // tslint:disable-next-line: object-literal-key-quotes
          'DeviceId': this.authService.deviceId
          // ...this.ieFix
        }
      });

      let isRefreshRequest = false;
      let isAnAsset = false;
      let isGoogleAnalytics = false;

      if (request.url.toLowerCase().indexOf('/refresh') > -1) isRefreshRequest = true;
      if (request.url.toLowerCase().indexOf('/assets/') > -1) isAnAsset = true;
      if (request.url.toLowerCase().indexOf('google') > -1) isGoogleAnalytics = true;

      if ((this.authService.hasAccessTokenExpired && !this.authService.hasRefreshTokenExpired && this.authService.access_token !== '' && !isRefreshRequest) && !isAnAsset && !isGoogleAnalytics) {
          return this.handleRefresh(request, next);
      }
    } else {
      request = request.clone({
        setHeaders: {
          // tslint:disable-next-line: object-literal-key-quotes
          'DeviceId': this.authService.deviceId
          // ...this.ieFix
        }
      });
    }
    return next.handle(request).pipe(catchError(error => this.handleAuthError(error)));
  }

  private handleRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      // First request to trigger refresh - do the actual refresh
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return new Observable<HttpEvent<any>>(observer => {
        this.authService.refreshToken(this.authService.refresh_token)
          .then(res => {
            if (res && res.token !== undefined) {
              // Refresh succeeded - update tokens and notify waiting requests
              this.authService.setUserAsLoggedIn(res, true);
              this.refreshTokenSubject.next(res.token);

              // Retry the original request with new token
              const retryRequest = request.clone({
                setHeaders: {
                  Authorization: `Bearer ${res.token}`,
                }
              });
              next.handle(retryRequest).subscribe(observer);
            } else {
              // Refresh failed - force logout to break the loop (no API call)
              this.authService.forceLogout();
              observer.error(new Error('Token refresh failed'));
            }
          })
          .catch(() => {
            // Refresh request failed (e.g., 401) - force logout to break the loop (no API call)
            this.authService.forceLogout();
            observer.error(new Error('Token refresh failed'));
          })
          .finally(() => {
            this.isRefreshing = false;
          });
      });
    } else {
      // Another request is already refreshing - wait for it to complete
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          // Retry request with the new token
          const retryRequest = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            }
          });
          return next.handle(retryRequest);
        })
      );
    }
  }

  handleAuthError(err: HttpErrorResponse): Observable<any> {
    if (err.status === 401) {
      this.authService.logoutUser();
      return of(err.message);
    }

    throw err;
  }
}
